'use client';

import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type FocusEvent,
} from 'react';
import Script from 'next/script';

import {OnePaneLayoutContainer} from '@udemy-v2/react-layout-components';
import {CompactFormGroup, Select, TextInput, TextInputForm} from '@udemy-v2/react-form-components';
import {LocalizedHtml, useFormatNumber, useI18n} from '@udemy-v2/i18n';
import {ToasterStore} from '@udemy-v2/react-notice-components';
import * as Sentry from '@sentry/nextjs';
import {useTracker} from '@udemy-v2/event-tracking';

import {isBusinessEmail, isValidEmail} from '@/lib/email-validation';
import styles from '@/features/organization-team-plan/organization-signup-form.module.scss';
import {collectMarketoTrackingFields} from '@/features/organization-team-plan/utils/collect-marketo-tracking';
import {syncUtmWithCookie} from '@/features/organization-team-plan/utils/sync-utm-with-cookie';
import type {SubmitTeamOrganizationResult} from '@/app/api/team-organization-signup/route';
import {
    publishTeamPlanLeadFormSubmissionFailed,
    publishTeamPlanLeadFormInteracted,
    publishTeamPlanLeadFormPresented,
    publishTeamPlanLeadFormSubmitted,
    publishTeamPlanLeadFormSubmissionSucceeded,
} from '@/features/organization-team-plan/utils/amplitude/publish-team-plan-lead-form-events';
import {
    UDEMY_BUSINESS_PRIVACY_STATEMENT_URL,
    UDEMY_MASTER_SERVICES_AGREEMENT_URL,
} from '@/features/organization-team-plan/utils/udemy-legal-urls';
import {fireTeamsFormSuccessEvent} from '@/features/organization-team-plan/gtm/team-plan-signup-gtm-events';
import {JAPAN_JOB_LEVEL_OPTIONS} from '@/features/organization-team-plan/utils/japan-job-level-options';
import {isJapanLocale} from '@/features/organization-team-plan/utils/is-japan-locale';

interface OrganizationSignupFormProps {
    buyNow: boolean;
    /** Udemy locale (e.g. ja_JP) for Marketo form routing; defaults to en_US in tests. */
    locale?: string;
    // This is the country code fetched from headers or cookies
    countryCode?: string;
}

interface OptionLowHigh {
    low: number;
    high: number;
}

interface OptionMin {
    min: number;
}

/**
 * License `<option>` row: stable `value` for the form; label derived from optional bounds.
 * - `range` with `low` + `high` → localized range label
 * - `range` with `min` only → localized "min+" label
 * - no `range` → {@link gettext}('Just myself')
 */
interface LicenseSelectOption {
    value: string;
    range?: OptionLowHigh | OptionMin;
}

/** Tracks inline validation message per field (`undefined` = no error). */
interface SignupFieldErrors {
    email?: string;
    firstName?: string;
    lastName?: string;
    department?: string;
    phoneNumber?: string;
    company?: string;
    employees?: string;
    identifier?: string;
    password?: string;
    jobLevel?: string;
}

/** Controlled values for gating submit until the form is complete. */
interface SignupFormFields {
    email: string;
    firstName: string;
    lastName: string;
    department: string;
    phoneNumber: string;
    company: string;
    employees: string;
    identifier: string;
    password: string;
    jobLevel: string;
}

type SignupGettext = ReturnType<typeof useI18n>['gettext'];
type SignupPgettext = ReturnType<typeof useI18n>['pgettext'];

const TURNSTILE_TIMEOUT = 30000;

/** Minimum character length for the password field (`minLength` and note text). */
const PASSWORD_MIN_LENGTH = 6;

const SIGNUP_FORM_ID = 'cta:signUp';

const SIGNUP_SUBMIT_ERROR_MSGID = 'Something went wrong. Please try again in a few moments.';

const INITIAL_FORM_FIELDS: SignupFormFields = {
    email: '',
    firstName: '',
    lastName: '',
    department: '',
    phoneNumber: '',
    company: '',
    employees: '',
    identifier: '',
    password: '',
    jobLevel: '',
};

/**
 * Fields that share "required on blur" validation. `name` on the control must match the
 * {@link SignupFieldErrors} key so one handler can update state.
 */
const REQUIRED_EMPTY_ON_BLUR_FIELDS = [
    'firstName',
    'lastName',
    'department',
    'phoneNumber',
    'company',
    'employees',
    'identifier',
    'jobLevel',
] as const;

type RequiredEmptyOnBlurField = (typeof REQUIRED_EMPTY_ON_BLUR_FIELDS)[number];

/** Stable form/API values; labels are formatted per locale in the component. */
const LICENSE_SELECT_OPTIONS: LicenseSelectOption[] = [
    {value: '2001+', range: {min: 2001}},
    {value: '201-2000', range: {low: 201, high: 2000}},
    {value: '51-200', range: {low: 51, high: 200}},
    {value: '21-50', range: {low: 21, high: 50}},
    {value: '11-20', range: {low: 11, high: 20}},
    {value: '2-10', range: {low: 2, high: 10}},
    {value: 'Just Myself'},
];

function getJapanJobLevelOptionLabel(
    value: (typeof JAPAN_JOB_LEVEL_OPTIONS)[number],
    pgettext: SignupPgettext,
): string {
    const labels: Record<(typeof JAPAN_JOB_LEVEL_OPTIONS)[number], string> = {
        'Individual Contributor': pgettext('Job Level option', 'Individual Contributor'),
        'Manager':               pgettext('Job Level option', 'Manager'),
        'Director / Sr':         pgettext('Job Level option', 'Director / Sr'),
        'VP':                    pgettext('Job Level option', 'VP'),
        'C-Level':               pgettext('Job Level option', 'C-Level'),
        'Other':                 pgettext('Job Level option', 'Other'),
    };
    return labels[value];
}

function createEmptySignupErrors(): SignupFieldErrors {
    return {
        email: undefined,
        firstName: undefined,
        lastName: undefined,
        department: undefined,
        phoneNumber: undefined,
        company: undefined,
        employees: undefined,
        identifier: undefined,
        password: undefined,
        jobLevel: undefined,
    };
}

/**
 * Controlled Udemy form controls may not populate native FormData on submit; mirror {@link SignupFormFields} into FormData for API + Marketo payloads.
 */
function buildSignupFormData(fields: SignupFormFields): FormData {
    const formData = new FormData();
    formData.set('email', fields.email.trim());
    formData.set('firstName', fields.firstName.trim());
    formData.set('lastName', fields.lastName.trim());
    formData.set('department', fields.department.trim());
    formData.set('phoneNumber', fields.phoneNumber.trim());
    formData.set('company', fields.company.trim());
    formData.set('employees', fields.employees);
    formData.set('identifier', fields.identifier.trim());
    formData.set('password', fields.password);
    formData.set('jobLevel', fields.jobLevel);
    return formData;
}

function isSignupFormComplete(fields: SignupFormFields, isJapan: boolean): boolean {
    return (
        fields.email.trim() !== '' &&
        fields.firstName.trim() !== '' &&
        fields.lastName.trim() !== '' &&
        fields.department.trim() !== '' &&
        fields.phoneNumber.trim() !== '' &&
        fields.company.trim() !== '' &&
        fields.employees !== '' &&
        fields.identifier.trim() !== '' &&
        fields.password.length >= PASSWORD_MIN_LENGTH &&
        (!isJapan || fields.jobLevel !== '')
    );
}

/** True when any field still has an inline validation message (e.g. invalid email after blur). */
function hasActiveSignupErrors(errors: SignupFieldErrors): boolean {
    return Object.values(errors).some((message) => message !== undefined);
}

/**
 * Inline work-email validation (trimmed). Used on blur, for submit gating, and before POST
 * so personal/invalid addresses cannot slip through without a blur event.
 */
function getWorkEmailFieldError(email: string, gettext: SignupGettext): string | undefined {
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
        return gettext('Must be valid company email. "example@yourdomain.com"');
    }
    if (!isBusinessEmail(trimmed)) {
        return gettext('Please use your work email.');
    }
    return undefined;
}

function signupErrorsWithResolvedWorkEmail(
    errors: SignupFieldErrors,
    fields: SignupFormFields,
    gettext: SignupGettext,
): SignupFieldErrors {
    return {
        ...errors,
        email: getWorkEmailFieldError(fields.email, gettext) ?? errors.email,
    };
}

function isControlValueEmpty(target: EventTarget & (HTMLInputElement | HTMLSelectElement)) {
    return target instanceof HTMLSelectElement ? target.value === '' : target.value.trim() === '';
}

function isRequiredEmptyOnBlurField(name: string): name is RequiredEmptyOnBlurField {
    return (REQUIRED_EMPTY_ON_BLUR_FIELDS as readonly string[]).includes(name);
}

function getRequiredEmptyOnBlurMessage(
    field: RequiredEmptyOnBlurField,
    gettext: ReturnType<typeof useI18n>['gettext'],
): string {
    switch (field) {
        case 'firstName':
            return gettext('Please enter your first name.');
        case 'lastName':
            return gettext('Please enter your last name.');
        case 'department':
            return gettext('Please enter your job title.');
        case 'phoneNumber':
            return gettext('Please enter your phone number. 503-555-1212');
        case 'company':
            return gettext('Please enter your company name.');
        case 'employees':
            return gettext('Please select the number of user licenses needed.');
        case 'identifier':
            return gettext('Please enter your custom account subdomain.');
        case 'jobLevel':
            return gettext('Please select your job level.');
        default: {
            const _exhaustive: never = field;
            return _exhaustive;
        }
    }
}

function getLicenseOptionLabel(
    option: LicenseSelectOption,
    gettext: ReturnType<typeof useI18n>['gettext'],
    interpolate: ReturnType<typeof useI18n>['interpolate'],
    formatNumber: ReturnType<typeof useFormatNumber>['formatNumber'],
): string {
    const {range} = option;
    if (range === undefined) {
        return gettext('Just myself');
    }
    if ('min' in range) {
        return interpolate(gettext('%(min)s+'), {min: formatNumber(range.min)}, true);
    }
    return interpolate(
        gettext('%(low)s - %(high)s'),
        {low: formatNumber(range.low), high: formatNumber(range.high)},
        true,
    );
}

function formDataToMarketoLeadFields(formData: FormData) {
    const get = (key: string) => String(formData.get(key) ?? '').trim();
    return {
        email: get('email'),
        firstName: get('firstName'),
        lastName: get('lastName'),
        department: get('department'),
        phoneNumber: get('phoneNumber'),
        company: get('company'),
        employees: get('employees'),
        identifier: get('identifier'),
        jobLevel: get('jobLevel') || undefined,
    };
}

async function sendPostSignupEvents(
    email: string,
    formData: FormData,
    locale: string,
    buyNow: boolean,
): Promise<void> {
    try {
        const [gtmResult, marketoResult] = await Promise.allSettled([
            fireTeamsFormSuccessEvent(email, locale),
            fetch(`/${process.env.APP_NAME}/api/team-plan-marketo-sync/`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    lead: formDataToMarketoLeadFields(formData),
                    locale,
                    tracking: collectMarketoTrackingFields(buyNow),
                }),
            }).then((r) => {
                if (!r.ok) throw new Error(`Marketo sync failed: ${r.status}`);
                return r.json();
            }),
        ]);

        if (gtmResult.status === 'rejected') {
            Sentry.captureException(gtmResult.reason, {
                extra: {context: 'Post-signup GTM event failed'},
            });
        }
        if (marketoResult.status === 'rejected') {
            Sentry.captureException(marketoResult.reason, {
                extra: {context: 'Post-signup Marketo sync failed'},
            });
        }
    } catch (e) {
        Sentry.captureException(e, {
            extra: {context: 'Post-signup events failed unexpectedly'},
        });
    }
}

export function OrganizationSignupForm({buyNow, locale = 'en_US', countryCode = 'US'}: OrganizationSignupFormProps) {
    const tracker = useTracker();
    const {gettext, interpolate, ninterpolate, pgettext} = useI18n();
    const {formatNumber} = useFormatNumber();
    const [fields, setFields] = useState<SignupFormFields>(INITIAL_FORM_FIELDS);
    const [errors, setErrors] = useState<SignupFieldErrors>(createEmptySignupErrors);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const hasFormFieldBeenFocused = useRef(false);

    const turnstileContainerRef = useRef<HTMLDivElement>(null);
    const turnstileWidgetId = useRef<string | null>(null);
    const tokenCallbackRef = useRef<((token: string | null) => void) | null>(null);

    useEffect(() => {
        syncUtmWithCookie();
    }, []);

    useEffect(() => {
        publishTeamPlanLeadFormPresented(tracker, SIGNUP_FORM_ID);
    }, [tracker]);

    /**
     * Renders the invisible Turnstile widget inside the hidden container.
     */
    const renderTurnstileWidget = useCallback(() => {
        if (
            !window.turnstile ||
            !turnstileContainerRef.current ||
            turnstileWidgetId.current !== null
        ) {
            return;
        }
        const siteKey = window.PUBLIC_CONFIG?.['TURNSTILE_SITE_KEY'] ?? '';
        if (!siteKey) return;
        turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
            sitekey: siteKey,
            appearance: 'interaction-only',
            execution: 'execute',
            callback: (token) => {
                tokenCallbackRef.current?.(token);
                tokenCallbackRef.current = null;
            },
            'expired-callback': () => {
                tokenCallbackRef.current?.(null);
                tokenCallbackRef.current = null;
            },
            'error-callback': () => {
                tokenCallbackRef.current?.(null);
                tokenCallbackRef.current = null;
            },
        });
    }, []);

    useEffect(() => {
        renderTurnstileWidget();

        return () => {
            if (turnstileWidgetId.current !== null) {
                window.turnstile?.remove(turnstileWidgetId.current);
                turnstileWidgetId.current = null;
            }
        };
    }, [renderTurnstileWidget]);

    /**
     * Requests a fresh Turnstile token by calling execute() on the widget and awaiting the
     * callback. Returns null immediately when no widget is mounted (Turnstile not configured),
     * or after 30 s if the challenge does not resolve (times out or triggers error-callback)
     */
    const getTurnstileToken = useCallback((): Promise<string | null> => {
        if (turnstileWidgetId.current === null) return Promise.resolve(null);
        return new Promise((resolve) => {
            const timer = setTimeout(() => {
                tokenCallbackRef.current = null;
                resolve(null);
            }, TURNSTILE_TIMEOUT);
            tokenCallbackRef.current = (token) => {
                clearTimeout(timer);
                tokenCallbackRef.current = null;
                resolve(token);
            };
            window.turnstile?.execute(turnstileWidgetId.current!);
        });
    }, []);

    const handleFirstInteraction = useCallback(() => {
        if (hasFormFieldBeenFocused.current) return;
        hasFormFieldBeenFocused.current = true;
        publishTeamPlanLeadFormInteracted(tracker, SIGNUP_FORM_ID);
    }, [tracker]);

    const isJapan = isJapanLocale(locale);

    const canSubmit = useMemo(
        () =>
            isSignupFormComplete(fields, isJapan) &&
            !hasActiveSignupErrors(signupErrorsWithResolvedWorkEmail(errors, fields, gettext)),
        [errors, fields, gettext, isJapan],
    );

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const emailError = getWorkEmailFieldError(fields.email, gettext);
            if (emailError) {
                setErrors((prev) => ({...prev, email: emailError}));
                return;
            }

            setIsSubmitting(true);
            let shouldClearSubmittingState = true;
            let signupSucceeded = false;
            try {
                // If a site key is configured but the Turnstile script never loaded, the script
                // was blocked (ad-blocker, corporate firewall, network failure). Show an
                // actionable message and log to Sentry so we can measure how often this happens.
                if (window.PUBLIC_CONFIG?.['TURNSTILE_SITE_KEY'] && !window.turnstile) {
                    Sentry.captureMessage(
                        'Turnstile script failed to load — likely blocked by ad-blocker or firewall',
                        {level: 'warning'},
                    );
                    ToasterStore.addAlertBannerToast(
                        {
                            udStyle: 'error',
                            title: gettext(
                                "We couldn't load our verification service. Try disabling your ad-blocker or using a different network.",
                            ),
                            actionButtonProps: false,
                        },
                        {autoDismiss: true, autoDismissTimeout: 5000},
                    );
                    return;
                }

                // Obtain a fresh token at submit time (execution: 'execute' defers the challenge
                // until here).
                const freshToken = await getTurnstileToken();
                if (turnstileWidgetId.current !== null && freshToken === null) {
                    // Turnstile challenge failed or timed out — reset widget so it's ready
                    // for the next attempt, then abort without calling the API.
                    window.turnstile?.reset(turnstileWidgetId.current);
                    ToasterStore.addAlertBannerToast(
                        {
                            udStyle: 'error',
                            title: gettext('Verification failed. Please try again.'),
                            actionButtonProps: false,
                        },
                        {autoDismiss: true, autoDismissTimeout: 5000},
                    );
                    return;
                }

                const formData = buildSignupFormData(fields);
                publishTeamPlanLeadFormSubmitted(tracker, SIGNUP_FORM_ID);
                // Turnstile verification and monolith submission are combined in one API route call
                // so bots cannot bypass the captcha by calling the monolith endpoint directly.
                const signupResponse = await fetch(
                    `/${process.env.APP_NAME}/api/team-organization-signup/`,
                    {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({
                            email: fields.email.trim(),
                            firstName: fields.firstName.trim(),
                            lastName: fields.lastName.trim(),
                            department: fields.department.trim(),
                            company: fields.company.trim(),
                            employees: fields.employees,
                            identifier: fields.identifier.trim(),
                            password: fields.password,
                            turnstileToken: freshToken,
                            buyNow,
                            locale,
                            countryCode,
                        }),
                    },
                );
                // Token was consumed server-side (single-use); reset so the widget is ready
                // to execute() again for any subsequent submit attempt.
                if (turnstileWidgetId.current !== null) {
                    window.turnstile?.reset(turnstileWidgetId.current);
                }

                if (!signupResponse.ok) {
                    throw new Error(`Signup API returned ${signupResponse.status}`);
                }
                const result = (await signupResponse.json()) as SubmitTeamOrganizationResult;

                if (!result.success) {
                    if (result.reason === 'turnstile_failed') {
                        publishTeamPlanLeadFormSubmissionFailed(
                            tracker,
                            SIGNUP_FORM_ID,
                            'Verification failed',
                        );
                        ToasterStore.addAlertBannerToast(
                            {
                                udStyle: 'error',
                                title: gettext('Verification failed. Please try again.'),
                                actionButtonProps: false,
                            },
                            {autoDismiss: true, autoDismissTimeout: 5000},
                        );
                        return;
                    }

                    if (result.reason === 'kickbox_rejected') {
                        publishTeamPlanLeadFormSubmissionFailed(
                            tracker,
                            SIGNUP_FORM_ID,
                            'Kickbox rejected email',
                        );
                        setErrors((prev) => ({
                            ...prev,
                            email: gettext(
                                "We couldn't verify this email address. Please use a valid work email.",
                            ),
                        }));
                        ToasterStore.addAlertBannerToast(
                            {
                                udStyle: 'error',
                                title: gettext('Please review and correct the form errors.'),
                                actionButtonProps: false,
                            },
                            {autoDismiss: true, autoDismissTimeout: 5000},
                        );
                        return;
                    }

                    publishTeamPlanLeadFormSubmissionFailed(
                        tracker,
                        SIGNUP_FORM_ID,
                        result.reason === 'api_error' ? `HTTP ${result.status}` : 'Network error',
                    );

                    if (result.reason === 'api_error') {
                        const newErrors: SignupFieldErrors = {};
                        const {detail} = result;

                        if (detail.login?.error) {
                            newErrors.email = detail.login.msg;
                        }
                        if (detail.portal?.error) {
                            const msg = detail.portal.msg;
                            const suggestion = detail.portal.suggestion;
                            newErrors.identifier = suggestion ? `${msg} Try: ${suggestion}` : msg;
                        }
                        if (detail.company?.error) {
                            newErrors.company = detail.company.msg;
                        }

                        setErrors((prev) => ({...prev, ...newErrors}));

                        ToasterStore.addAlertBannerToast(
                            {
                                udStyle: 'error',
                                title: gettext('Please review and correct the form errors.'),
                                actionButtonProps: false,
                            },
                            {autoDismiss: true, autoDismissTimeout: 5000},
                        );
                        return;
                    }

                    ToasterStore.addAlertBannerToast(
                        {
                            udStyle: 'error',
                            title: gettext(SIGNUP_SUBMIT_ERROR_MSGID),
                            actionButtonProps: false,
                        },
                        {autoDismiss: true, autoDismissTimeout: 5000},
                    );
                    return;
                }

                signupSucceeded = true;
                publishTeamPlanLeadFormSubmissionSucceeded(tracker, SIGNUP_FORM_ID);
                ToasterStore.addAlertBannerToast(
                    {
                        udStyle: 'success',
                        title: gettext('Account created successfully!'),
                        actionButtonProps: false,
                    },
                    {autoDismiss: true, autoDismissTimeout: 5000},
                );
                const {redirectUrl} = result;
                await sendPostSignupEvents(fields.email.trim(), formData, locale, buyNow);
                if (redirectUrl) {
                    shouldClearSubmittingState = false;
                    window.location.assign(redirectUrl);
                }
            } catch {
                if (!signupSucceeded) {
                    publishTeamPlanLeadFormSubmissionFailed(
                        tracker,
                        SIGNUP_FORM_ID,
                        'Network error',
                    );
                }
                ToasterStore.addAlertBannerToast(
                    {
                        udStyle: 'error',
                        title: gettext(SIGNUP_SUBMIT_ERROR_MSGID),
                        dismissButtonProps: false,
                        actionButtonProps: false,
                    },
                    {
                        autoDismiss: true,
                        autoDismissTimeout: 5000,
                    },
                );
            } finally {
                if (shouldClearSubmittingState) {
                    setIsSubmitting(false);
                }
            }
        },
        [fields, gettext, getTurnstileToken, tracker, buyNow, locale, countryCode],
    );

    const handleRequiredFieldBlur = useCallback(
        (event: FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
            const {name} = event.target;
            if (!isRequiredEmptyOnBlurField(name)) {
                return;
            }
            const emptyMessage = getRequiredEmptyOnBlurMessage(name, gettext);
            setErrors((prev) => ({
                ...prev,
                [name]: isControlValueEmpty(event.target) ? emptyMessage : undefined,
            }));
        },
        [gettext],
    );

    const handleNonPasswordTextInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const field = event.target.name as keyof SignupFieldErrors;
        setFields((prev) => ({...prev, [field]: event.target.value}));
        setErrors((prev) =>
            prev[field] !== undefined && event.target.value.trim() !== ''
                ? {...prev, [field]: undefined}
                : prev,
        );
    }, []);

    const handleWorkEmailBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            setErrors((prev) => ({
                ...prev,
                email: getWorkEmailFieldError(event.target.value, gettext),
            }));
        },
        [gettext],
    );

    const handleSelectChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
        const field = event.target.name as keyof SignupFieldErrors;
        setFields((prev) => ({...prev, [field]: event.target.value}));
        setErrors((prev) =>
            prev[field] !== undefined && event.target.value !== ''
                ? {...prev, [field]: undefined}
                : prev,
        );
    }, []);

    const handlePasswordBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            const {value} = event.target;
            if (value.trim() === '') {
                setErrors((prev) => ({...prev, password: gettext('Please enter your password.')}));
            } else if (value.length < PASSWORD_MIN_LENGTH) {
                setErrors((prev) => ({
                    ...prev,
                    password: ninterpolate(
                        'Password must be at least %(minLength)s character.',
                        'Password must be at least %(minLength)s characters.',
                        PASSWORD_MIN_LENGTH,
                        {minLength: formatNumber(PASSWORD_MIN_LENGTH)},
                    ),
                }));
            } else {
                setErrors((prev) => ({...prev, password: undefined}));
            }
        },
        [formatNumber, gettext, ninterpolate],
    );

    const handlePasswordChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const {value} = event.target;
            setFields((prev) => ({...prev, password: value}));
            const emptyPasswordMessage = gettext('Please enter your password.');
            setErrors((prev) => {
                if (prev.password === undefined) {
                    return prev;
                }
                if (value.trim() === '') {
                    return prev;
                }
                if (value.length >= PASSWORD_MIN_LENGTH) {
                    return {...prev, password: undefined};
                }
                if (prev.password === emptyPasswordMessage) {
                    return {...prev, password: undefined};
                }
                return prev;
            });
        },
        [gettext],
    );

    const submitButtonContent = buyNow ? gettext('Buy Team Plan') : gettext('Start free trial');
    const passwordNote = ninterpolate(
        'Minimum of %(minLength)s character',
        'Minimum of %(minLength)s characters',
        PASSWORD_MIN_LENGTH,
        {minLength: formatNumber(PASSWORD_MIN_LENGTH)},
    );
    const subdomainGuidanceNote = gettext('Your team URL, e.g. yourcompany.udemy.com');

    return (
        <OnePaneLayoutContainer className={styles.container}>
            <TextInputForm
                className={styles['submit-button-text']}
                submitButtonContent={submitButtonContent}
                disabled={isSubmitting || !canSubmit}
                onSubmit={handleSubmit}
            >
                <div
                    className={styles['signup-form-content']}
                    onFocusCapture={handleFirstInteraction}
                >
                    <CompactFormGroup
                        label={gettext('Work email')}
                        note={errors.email ?? undefined}
                        validationState={errors.email ? 'error' : undefined}
                    >
                        <TextInput
                            type="email"
                            name="email"
                            required
                            value={fields.email}
                            onBlur={handleWorkEmailBlur}
                            onChange={handleNonPasswordTextInputChange}
                        />
                    </CompactFormGroup>
                    <div className={styles['signup-form-row']}>
                        <CompactFormGroup
                            label={gettext('First name')}
                            note={errors.firstName ?? undefined}
                            validationState={errors.firstName ? 'error' : undefined}
                        >
                            <TextInput
                                name="firstName"
                                required
                                value={fields.firstName}
                                onBlur={handleRequiredFieldBlur}
                                onChange={handleNonPasswordTextInputChange}
                            />
                        </CompactFormGroup>
                        <CompactFormGroup
                            label={gettext('Last name')}
                            note={errors.lastName ?? undefined}
                            validationState={errors.lastName ? 'error' : undefined}
                        >
                            <TextInput
                                name="lastName"
                                required
                                value={fields.lastName}
                                onBlur={handleRequiredFieldBlur}
                                onChange={handleNonPasswordTextInputChange}
                            />
                        </CompactFormGroup>
                    </div>
                    <div className={styles['signup-form-row']}>
                        <CompactFormGroup
                            label={gettext('Job title')}
                            note={errors.department ?? undefined}
                            validationState={errors.department ? 'error' : undefined}
                        >
                            <TextInput
                                name="department"
                                required
                                value={fields.department}
                                onBlur={handleRequiredFieldBlur}
                                onChange={handleNonPasswordTextInputChange}
                            />
                        </CompactFormGroup>
                        <CompactFormGroup
                            label={gettext('Phone number')}
                            note={errors.phoneNumber ?? undefined}
                            validationState={errors.phoneNumber ? 'error' : undefined}
                        >
                            <TextInput
                                type="tel"
                                name="phoneNumber"
                                required
                                value={fields.phoneNumber}
                                onBlur={handleRequiredFieldBlur}
                                onChange={handleNonPasswordTextInputChange}
                            />
                        </CompactFormGroup>
                    </div>
                    <div className={styles['signup-form-row']}>
                        <CompactFormGroup
                            label={gettext('Company name')}
                            note={errors.company ?? undefined}
                            validationState={errors.company ? 'error' : undefined}
                        >
                            <TextInput
                                name="company"
                                required
                                value={fields.company}
                                onBlur={handleRequiredFieldBlur}
                                onChange={handleNonPasswordTextInputChange}
                            />
                        </CompactFormGroup>
                        <CompactFormGroup
                            label={gettext('Number of user licenses needed')}
                            note={errors.employees ?? undefined}
                            validationState={errors.employees ? 'error' : undefined}
                        >
                            <Select
                                name="employees"
                                required
                                value={fields.employees}
                                onBlur={handleRequiredFieldBlur}
                                onChange={handleSelectChange}
                            >
                                <Select.Placeholder>{gettext('Select...')}</Select.Placeholder>
                                {LICENSE_SELECT_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {getLicenseOptionLabel(
                                            opt,
                                            gettext,
                                            interpolate,
                                            formatNumber,
                                        )}
                                    </option>
                                ))}
                            </Select>
                        </CompactFormGroup>
                    </div>
                    {isJapan && (
                        <CompactFormGroup
                            label={gettext('Job Level')}
                            note={errors.jobLevel ?? undefined}
                            validationState={errors.jobLevel ? 'error' : undefined}
                        >
                            <Select
                                name="jobLevel"
                                required
                                value={fields.jobLevel}
                                onBlur={handleRequiredFieldBlur}
                                onChange={handleSelectChange}
                            >
                                <Select.Placeholder>{gettext('Select...')}</Select.Placeholder>
                                {JAPAN_JOB_LEVEL_OPTIONS.map((value) => (
                                    <option key={value} value={value}>
                                        {getJapanJobLevelOptionLabel(value, pgettext)}
                                    </option>
                                ))}
                            </Select>
                        </CompactFormGroup>
                    )}
                    <div className={styles['subdomain-row']}>
                        <CompactFormGroup
                            label={
                                <>
                                    <span className={styles['subdomain-label-full']}>
                                        {gettext('Custom account subdomain')}
                                    </span>
                                    <span className={styles['subdomain-label-short']}>
                                        {gettext('Subdomain')}
                                    </span>
                                </>
                            }
                            className={styles['subdomain-input-wrapper']}
                            note={errors.identifier ?? subdomainGuidanceNote}
                            validationState={errors.identifier ? 'error' : undefined}
                        >
                            <TextInput
                                name="identifier"
                                required
                                value={fields.identifier}
                                onBlur={handleRequiredFieldBlur}
                                onChange={handleNonPasswordTextInputChange}
                            />
                            <span className={styles['subdomain-suffix']}>.udemy.com</span>
                        </CompactFormGroup>
                    </div>
                    <CompactFormGroup
                        label={gettext('Your password')}
                        note={errors.password ?? passwordNote}
                        validationState={errors.password ? 'error' : undefined}
                    >
                        <TextInput
                            type="password"
                            minLength={PASSWORD_MIN_LENGTH}
                            name="password"
                            required
                            value={fields.password}
                            onBlur={handlePasswordBlur}
                            onChange={handlePasswordChange}
                        />
                    </CompactFormGroup>
                    <LocalizedHtml
                        className={styles['signup-text']}
                        html={gettext(
                            'By signing up, you agree to our <a class="msa">Master Services Agreement</a> and <a class="ubPrivacy">Udemy Business Privacy Statement</a>.',
                        )}
                        interpolate={{
                            msa: (
                                <a
                                    href={UDEMY_MASTER_SERVICES_AGREEMENT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                />
                            ),
                            ubPrivacy: (
                                <a
                                    href={UDEMY_BUSINESS_PRIVACY_STATEMENT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                />
                            ),
                        }}
                    />
                </div>
                <div ref={turnstileContainerRef} aria-hidden="true" />
            </TextInputForm>
            <Script
                src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
                strategy="afterInteractive"
                onLoad={renderTurnstileWidget}
            />
        </OnePaneLayoutContainer>
    );
}
